import { Button, Group, Text } from "@mantine/core";

import { withPolymorphicClickAnalytics } from "@/analytics";
import { useCallbacksRemaining } from "@/sdk";
import { useCallbackStore } from "@/sdk/sessions/callback.store";

import type { CallbackFormVariant } from "../../types";

import { useControlsStore } from "../../../CallControls/controls.store";
import { useCallbacksFormStore } from "../../callbacksForm.store";
import { useScheduleCallback } from "../../useScheduleCallback";
import { callbackActionConfig } from "../../utilities";

export function CallbackActions({
    onBackClick,
    onSubmit,
    isSubmitting,
    formVariant,
    reachedCallbackCutoff,
    isCallbackMdnValid,
}: {
    onBackClick?: () => void;
    onSubmit: () => Promise<void>;
    formVariant: CallbackFormVariant;
    isSubmitting: boolean;
    isCallbackMdnValid: boolean;
    reachedCallbackCutoff?: boolean;
}) {
    const callbackType = useCallbacksFormStore((state) => state.callbackType);
    const selectedCallbackNowDelay = useCallbacksFormStore((state) => state.selectedCallbackNowDelay);
    const { callbackState } = useCallbackStore();
    const { canCallback } = useCallbacksRemaining();
    const { cancelScheduledCallback } = useScheduleCallback();

    const AnalyticsButton = withPolymorphicClickAnalytics(Button<"button">, "Click");
    const shouldShowUpdateButtons = callbackState && formVariant !== "post-call";
    return (
        <Group id="callback-actions" justify="flex-end" style={{ flexDirection: "column", flex: 1, width: "100%" }}>
            <AnalyticsButton
                data-testid={callbackActionConfig.schedule.testId}
                analytics={{
                    actionId: callbackActionConfig.schedule.actionId,
                    eventData: {
                        callbackType,
                        callbackDelay: selectedCallbackNowDelay,
                    },
                }}
                title={"Schedule callback"}
                disabled={isSubmitting || !canCallback || reachedCallbackCutoff || !isCallbackMdnValid}
                loading={isSubmitting}
                onClick={() => void onSubmit()}
                size="xs"
                w="100%"
                fz="xs"
                variant="filled"
            >
                Schedule
            </AnalyticsButton>
            {shouldShowUpdateButtons ? (
                <AnalyticsButton
                    data-testid={callbackActionConfig.cancel.testId}
                    analytics={{
                        actionId: callbackActionConfig.cancel.actionId,
                    }}
                    title="Cancel callback"
                    disabled={!canCallback || reachedCallbackCutoff}
                    onClick={cancelScheduledCallback}
                    size="xs"
                    w="100%"
                    fz="xs"
                    color="gray.0"
                    variant="outline"
                >
                    Cancel callback
                </AnalyticsButton>
            ) : null}
            {(callbackState ?? onBackClick) ? (
                <AnalyticsButton
                    data-testid={callbackActionConfig.back.testId}
                    analytics={{
                        actionId: callbackActionConfig.back.actionId,
                    }}
                    title="Back"
                    onClick={onBackClick ? onBackClick : useControlsStore.getState().closeCallbackPanel}
                    size="xs"
                    w="100%"
                    color="gray.0"
                    variant="subtle"
                >
                    <Text fz="xs" td="underline">
                        Back
                    </Text>
                </AnalyticsButton>
            ) : null}
        </Group>
    );
}
